import {
  AxiosError,
  type AxiosResponse,
  type RawAxiosRequestConfig,
} from "axios";
import { nextTick, onBeforeUnmount, ref } from "vue";

export type ChatStreamOptions = {
  onText?: (text: string) => void;
  signal?: AbortSignal;
  onError?: () => void;
};

export const measureChatPaint = (name: string, started: number) => {
  void nextTick(() =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        performance.measure(name, { start: started, end: performance.now() });
      }),
    ),
  );
};

// Use the generated client and its authenticated XHR transport so token refresh,
// tenant invalidation and request cancellation keep their normal behavior.
export const receiveChatStream = async <T>(
  request: (options: RawAxiosRequestConfig) => Promise<AxiosResponse>,
  { onText, signal, onError }: ChatStreamOptions = {},
): Promise<T> => {
  let offset = 0;
  let complete = false;
  let result: T;
  let failure: Error | undefined;
  let text = "";
  const consume = (responseText: string) => {
    if (failure || signal?.aborted) return;
    try {
      let end: number;
      while ((end = responseText.indexOf("\n", offset)) !== -1) {
        const line = responseText.slice(offset, end);
        offset = end + 1;
        if (!line.trim()) continue;
        const event = JSON.parse(line);
        if (complete) throw new Error("Unexpected event after chat completion");
        if (event.type === "delta" && typeof event.text === "string") {
          text += event.text;
          onText?.(text);
        } else if (event.type === "done") {
          result = event.data;
          complete = true;
        } else if (event.type === "error") {
          const error = new AxiosError("Chat generation failed");
          error.response = { status: event.status } as AxiosResponse;
          throw error;
        } else if (event.type !== "ping") {
          throw new Error("Invalid chat stream event");
        }
      }
    } catch (error) {
      failure =
        error instanceof Error ? error : new Error("Invalid chat stream");
    }
  };
  try {
    const response = await request({
      headers: { Accept: "application/x-ndjson" },
      adapter: "xhr",
      responseType: "text",
      signal,
      onDownloadProgress: (progress) => {
        const responseText = progress.event?.target?.responseText;
        if (typeof responseText === "string") consume(responseText);
      },
    });
    consume(response.data);
    if (failure) throw failure;
    if (!complete) throw new Error("Chat stream ended before completion");
    return result!;
  } catch (error) {
    if (!signal?.aborted) onError?.();
    throw error;
  }
};

export const useChatDraft = () => {
  const draft = ref("");
  const active = ref(false);
  const error = ref("");
  let controller: AbortController | undefined;
  let started = 0;
  const cancel = () => {
    controller?.abort();
    active.value = false;
    draft.value = "";
  };
  const begin = (): ChatStreamOptions => {
    cancel();
    controller = new AbortController();
    active.value = true;
    error.value = "";
    const requestStarted = performance.now();
    started = requestStarted;
    let measuredFirstText = false;
    return {
      signal: controller.signal,
      onText: (text) => {
        draft.value = text;
        if (!measuredFirstText && text.trim()) {
          measuredFirstText = true;
          measureChatPaint("chat-first-visible-text", requestStarted);
        }
      },
      onError: () => {
        draft.value = "";
        error.value = "The answer could not be completed. Please try again.";
      },
    };
  };
  const finish = () => {
    const completed = active.value && !error.value;
    active.value = false;
    draft.value = "";
    controller = undefined;
    if (completed) measureChatPaint("chat-ui-completion", started);
  };
  onBeforeUnmount(cancel);
  return { draft, error, active, begin, cancel, finish };
};
